/**
 * Tokens of users who have signed off this device but still have unsynced
 * changes lying in the local database.
 *
 * A user switch in the field happens offline: the outgoing user's edits cannot
 * be flushed first, and pushing them later under the *new* user's token would
 * file them in the version history under the wrong name. So their token is kept
 * until their queue drains, and the replication push sends their documents with
 * it (see `db/authorJournal`) — the client never gets to *claim* an author.
 *
 * The entry outlives its own token on purpose: when the token expires or the
 * server rejects it, the name and username stay so the app can ask that person
 * to sign in again (see `db/syncBlockers`). Only an empty queue drops it.
 */

import { defineStoredJson } from './storage';
import { jwtPayload } from './jwtPayload';

interface Entry {
    /** Absent once the token expired or was rejected — the queue then blocks. */
    token?: string;
    /** Login name, so the sign-in prompt knows which account to check for. */
    username: string;
    /** What to call them in that prompt. */
    name: string;
}

type Vault = Record<string, Entry>;

/**
 * Never a cache: what is in here is the only way somebody else's queued work
 * still goes up under their own name.
 */
const vaultValue = defineStoredJson<Vault>({
    key: 'pending_credentials',
    fallback: () => ({}),
    keepOnWipe: true
});

function read(): Vault {
    return vaultValue.read();
}

function write(vault: Vault): void {
    // Storage full or blocked — the push then treats this user as blocked,
    // which prompts for their sign-in rather than mis-crediting anything.
    if (Object.keys(vault).length === 0) {
        vaultValue.clear();
        return;
    }
    vaultValue.write(vault);
}

/** A JWT's expiry in ms, or 0 when it cannot be read. */
function expiryOf(token: string): number {
    const exp = jwtPayload(token)?.exp;
    return typeof exp === 'number' ? exp * 1000 : 0;
}

/** Hold on to `token` so `userId`'s queued changes can still go up as them. */
export function keepCredential(
    userId: string,
    token: string,
    username: string,
    name: string
): void {
    if (!userId || !token) {
        return;
    }
    const vault = read();
    vault[userId] = { token, username, name };
    write(vault);
}

/**
 * The usable token for `userId`, or null when there is none or it has expired.
 * The entry itself survives, so the app can still name them when asking for a
 * fresh sign-in.
 */
export function credentialFor(userId: string): string | null {
    const entry = read()[userId];
    if (!entry?.token) {
        return null;
    }
    const expiry = expiryOf(entry.token);
    if (expiry && expiry <= Date.now()) {
        invalidateCredential(userId);
        return null;
    }
    return entry.token;
}

/** Who this pending author is, for the sign-in prompt. */
export function credentialIdentity(
    userId: string
): { username: string; name: string } | null {
    const entry = read()[userId];
    return entry ? { username: entry.username, name: entry.name } : null;
}

/** Drop only the token — the server refused it or it aged out. */
export function invalidateCredential(userId: string): void {
    const vault = read();
    const entry = vault[userId];
    if (!entry?.token) {
        return;
    }
    delete entry.token;
    vault[userId] = entry;
    write(vault);
}

/** Forget a user entirely — their queue has drained. */
export function dropCredential(userId: string): void {
    const vault = read();
    if (!(userId in vault)) {
        return;
    }
    delete vault[userId];
    write(vault);
}
